import { resolveFeatherPx } from './spotlight-feather';

/** One highlight window during a camera flight, in screen pixels relative to the painter's canvas. */
export interface FlightWindow {
  x: number;
  y: number;
  width: number;
  height: number;
  /** How much of the shading the window removes: 1 fully clear, 0 not at all. */
  clearness: number;
  /** Whether to draw the outline the spotlight draws for several or labelled regions. */
  outlined: boolean;
}

/** Outline colours and sizes, the same as the spotlight overlay's stylesheet. */
const OUTLINE_OUTSET_PX = 3;
const OUTLINE_SHADOW = { color: 'rgba(20, 20, 20, 0.55)', width: 5 };
const OUTLINE = { color: '#5298e3', width: 2.5 };

/**
 * Draws the spotlight shading on a viewport-sized canvas while the camera flies (Issue #3127).
 *
 * During a flight the page and the spotlight SVG are scaled as composited layers. The SVG is a
 * page-sized picture with blurred windows; scaled several times over it shows enlarged pixels, and
 * the browser re-rasterizes it tile by tile, so the shading flickers and shows blank or blocky
 * rectangles. This painter instead draws the shading afresh every frame at screen resolution: one
 * dark fill and a soft-edged hole per window, cut with the same feather the SVG uses (a rectangle
 * grown by the feather and blurred by a fifth of it). The SVG is hidden for the flight and takes
 * over again at landing.
 */
export class SpotlightFlightPainter {
  private context: CanvasRenderingContext2D | null = null;
  private pixelRatio = 1;

  constructor(private readonly canvas: HTMLCanvasElement) {}

  /** Size the canvas to a box in CSS pixels, sharp on high-density screens. */
  resize(left: number, top: number, width: number, height: number, pixelRatio: number): void {
    this.pixelRatio = pixelRatio > 0 && Number.isFinite(pixelRatio) ? pixelRatio : 1;
    const style = this.canvas.style;
    style.left = `${left}px`;
    style.top = `${top}px`;
    style.width = `${width}px`;
    style.height = `${height}px`;
    this.canvas.width = Math.max(1, Math.round(width * this.pixelRatio));
    this.canvas.height = Math.max(1, Math.round(height * this.pixelRatio));
    this.context = this.canvas.getContext('2d');
  }

  /**
   * Draw one frame: the shading at `overlayOpacity` over the page, with every window cut out of it.
   * `page` is the page's rectangle on the canvas; the spotlight SVG shades only the page, so the
   * margins around it stay unshaded here too and nothing changes at the hand-over.
   */
  paint(
    windows: readonly FlightWindow[],
    page: { x: number; y: number; width: number; height: number },
    overlayOpacity: number,
    featherPercent: number
  ): void {
    const context = this.context;
    if (!context) return;
    const ratio = this.pixelRatio;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.globalCompositeOperation = 'source-over';
    context.filter = 'none';
    context.globalAlpha = 1;
    context.clearRect(0, 0, this.canvas.width / ratio, this.canvas.height / ratio);
    context.fillStyle = `rgba(0, 0, 0, ${overlayOpacity})`;
    context.fillRect(page.x, page.y, page.width, page.height);

    context.globalCompositeOperation = 'destination-out';
    context.fillStyle = '#000';
    for (const window of windows) {
      if (window.clearness <= 0 || window.width <= 0 || window.height <= 0) continue;
      const feather = resolveFeatherPx(featherPercent, window.width, window.height);
      context.globalAlpha = Math.min(1, window.clearness);
      context.filter = feather > 0 ? `blur(${feather / 5}px)` : 'none';
      context.fillRect(window.x - feather, window.y - feather, window.width + feather * 2, window.height + feather * 2);
    }

    context.globalCompositeOperation = 'source-over';
    context.filter = 'none';
    for (const window of windows) {
      if (!window.outlined || window.clearness <= 0) continue;
      context.globalAlpha = Math.min(1, window.clearness);
      for (const stroke of [OUTLINE_SHADOW, OUTLINE]) {
        context.strokeStyle = stroke.color;
        context.lineWidth = stroke.width;
        context.strokeRect(
          window.x - OUTLINE_OUTSET_PX,
          window.y - OUTLINE_OUTSET_PX,
          window.width + OUTLINE_OUTSET_PX * 2,
          window.height + OUTLINE_OUTSET_PX * 2
        );
      }
    }
    context.globalAlpha = 1;
  }

  /** Remove everything the painter drew. */
  clear(): void {
    const context = this.context;
    if (!context) return;
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
}
